import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo } from './Logo';
import { Icon, IconName } from '../ui/Icon';
import { CurrencySwitcher } from './CurrencySwitcher';
import { ThemeToggle } from './ThemeToggle';

type NavLinkDef = { label: string; path: string; icon: IconName };

const PUBLIC_LINKS: NavLinkDef[] = [
  { label: 'Ship a package', path: '/shipping', icon: 'package' },
  { label: 'Track shipment', path: '/track', icon: 'search' },
  { label: 'Services', path: '/services', icon: 'truck' },
  { label: 'Pricing', path: '/volumetric-weight', icon: 'scale' },
  { label: 'About', path: '/about', icon: 'building' },
];

const ACCOUNT_LINKS: NavLinkDef[] = [
  { label: 'Dashboard', path: '/overview', icon: 'home' },
  { label: 'My orders', path: '/orders', icon: 'receipt' },
  { label: 'Wallet', path: '/wallet', icon: 'wallet' },
  { label: 'Contacts', path: '/contacts', icon: 'users' },
  { label: 'Profile', path: '/profile', icon: 'user' },
];

export const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  React.useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Close menu on Escape
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const userProfile = user as {
    firstName?: string; lastName?: string;
    first_name?: string; last_name?: string;
    email?: string;
  } | null;

  const initials = React.useMemo(() => {
    if (!userProfile) return 'U';
    const f = userProfile.firstName?.[0] ?? userProfile.first_name?.[0] ?? '';
    const l = userProfile.lastName?.[0] ?? userProfile.last_name?.[0] ?? '';
    return (f + l || 'U').toUpperCase();
  }, [userProfile]);

  const fullName = React.useMemo(() => {
    if (!userProfile) return '';
    const f = userProfile.firstName ?? userProfile.first_name ?? '';
    const l = userProfile.lastName ?? userProfile.last_name ?? '';
    return `${f} ${l}`.trim();
  }, [userProfile]);

  const handleLogout = async () => {
    setOpen(false);
    try { await logout(); } catch { /* noop */ }
    navigate('/');
  };

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <header className={`nav ${scrolled ? 'scrolled' : ''} ${open ? 'menu-open' : ''}`}>
        <div className="nav-inner">
          <Logo />

          {/* Desktop primary links */}
          <nav className="nav-links" aria-label="Primary">
            {PUBLIC_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="nav-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm nav-track-btn"
              onClick={() => navigate('/track')}
            >
              <Icon name="search" size={15} />
              <span>Track</span>
            </button>
            <span className="currency-switcher-wrap"><CurrencySwitcher /></span>
            <span className="theme-toggle-wrap"><ThemeToggle /></span>
            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  className="btn btn-ghost btn-icon nav-bell"
                  aria-label="Notifications"
                  onClick={() => navigate('/orders')}
                >
                  <Icon name="bell" size={17} />
                </button>
                <Link to="/overview" className="btn btn-secondary btn-sm nav-dash-btn">
                  Dashboard
                </Link>
                <Link
                  to="/profile"
                  className="avatar nav-avatar"
                  style={{ cursor: 'pointer', textDecoration: 'none' }}
                  aria-label="Profile"
                >
                  {initials}
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm nav-logout-btn"
                  onClick={handleLogout}
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <Icon name="logout" size={15} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm nav-signin-btn">
                  Sign in
                </Link>
                <Link to="/signup" className="btn btn-primary btn-sm nav-signup-btn">
                  Open account
                </Link>
              </>
            )}

            {/* Mobile hamburger */}
            <button
              type="button"
              className="nav-burger mobile-only"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              <span className={`nav-burger-lines ${open ? 'is-open' : ''}`}>
                <span /><span /><span />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu — full-screen overlay drawer */}
      <div
        id="mobile-menu"
        className={`mnav ${open ? 'mnav-open' : ''}`}
        aria-hidden={!open}
      >
        <button
          type="button"
          className="mnav-scrim"
          aria-label="Close menu"
          tabIndex={open ? 0 : -1}
          onClick={() => setOpen(false)}
        />

        <aside className="mnav-panel" role="dialog" aria-modal="true" aria-label="Main menu">
          <div className="mnav-top">
            <Logo />
            <button
              type="button"
              className="mnav-close"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
            >
              <Icon name="x" size={20} />
            </button>
          </div>

          <div className="mnav-body">
            {/* Profile / welcome card */}
            {isAuthenticated ? (
              <button
                type="button"
                className="mnav-card mnav-card-profile"
                onClick={() => go('/profile')}
              >
                <div className="mnav-avatar">{initials}</div>
                <div className="mnav-card-text">
                  <div className="mnav-card-title">{fullName || 'Your account'}</div>
                  <div className="mnav-card-sub">
                    {userProfile?.email || 'View profile →'}
                  </div>
                </div>
                <Icon name="arrowRight" size={16} />
              </button>
            ) : (
              <div className="mnav-card mnav-card-welcome">
                <div className="mnav-card-text">
                  <div className="mnav-card-title">Ship smarter.</div>
                  <div className="mnav-card-sub">
                    Compare 24+ carriers in one place.
                  </div>
                </div>
                <div className="mnav-welcome-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-block"
                    onClick={() => go('/signup')}
                  >
                    Open account
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => go('/login')}
                  >
                    Sign in
                  </button>
                </div>
              </div>
            )}

            {/* Primary */}
            <div className="mnav-section">
              <div className="mnav-label">Explore</div>
              <nav className="mnav-list">
                {PUBLIC_LINKS.map((l) => (
                  <NavLink
                    key={l.path}
                    to={l.path}
                    className={({ isActive }) =>
                      `mnav-item ${isActive ? 'mnav-item-active' : ''}`
                    }
                    onClick={() => setOpen(false)}
                  >
                    <span className="mnav-item-icon">
                      <Icon name={l.icon} size={17} />
                    </span>
                    <span className="mnav-item-label">{l.label}</span>
                    <Icon name="arrowRight" size={14} />
                  </NavLink>
                ))}
              </nav>
            </div>

            {/* Account (authed only) */}
            {isAuthenticated && (
              <div className="mnav-section">
                <div className="mnav-label">Account</div>
                <nav className="mnav-list">
                  {ACCOUNT_LINKS.map((l) => (
                    <NavLink
                      key={l.path}
                      to={l.path}
                      className={({ isActive }) =>
                        `mnav-item ${isActive ? 'mnav-item-active' : ''}`
                      }
                      onClick={() => setOpen(false)}
                    >
                      <span className="mnav-item-icon">
                        <Icon name={l.icon} size={17} />
                      </span>
                      <span className="mnav-item-label">{l.label}</span>
                      <Icon name="arrowRight" size={14} />
                    </NavLink>
                  ))}
                </nav>
              </div>
            )}

            {/* Preferences */}
            <div className="mnav-section">
              <div className="mnav-label">Preferences</div>
              <div className="mnav-prefs">
                <div className="mnav-pref-row">
                  <div className="mnav-pref-label">
                    <Icon name="dollar" size={15} />
                    <span>Currency</span>
                  </div>
                  <CurrencySwitcher />
                </div>
                <div className="mnav-pref-row">
                  <div className="mnav-pref-label">
                    <Icon name="layers" size={15} />
                    <span>Theme</span>
                  </div>
                  <ThemeToggle />
                </div>
              </div>
            </div>
          </div>

          {/* Footer action */}
          {isAuthenticated && (
            <div className="mnav-foot">
              <button
                type="button"
                className="mnav-logout"
                onClick={handleLogout}
              >
                <Icon name="logout" size={16} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </aside>
      </div>
    </>
  );
};

export default Navbar;
